import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { updateAiModelColor } from "./aiModels.thunk";
// Served from `public/svg/` (not imported from `src/assets/`) so Vite never
// runs them through the asset pipeline: even a `?url` import gets inlined as
// a `data:` URI for a file this small in dev, and the app's `resolveLogoUrl`
// allowlist rejects `data:` URIs, silently falling back to the initials
// chip. A `public/` path is always served byte-for-byte, in dev and build.
const chatgptLogo = "/svg/aiAssistants/chatgpt.svg";
const geminiLogo = "/svg/aiAssistants/gemini.svg";
const claudeLogo = "/svg/aiAssistants/claude.svg";

type ModelVisuals = { logo: string; color: string };

// Icon + chart color live on the frontend for ChatGPT / Gemini / Claude
// until the API's logo URLs and colors are ready. Bootstrap still supplies
// id / name / displayName; `setAiModels` overlays these visuals on top.
const CHATGPT_VISUALS: ModelVisuals = { logo: chatgptLogo, color: "#40B599" };
const GEMINI_VISUALS: ModelVisuals = { logo: geminiLogo, color: "#689DF6" };
const CLAUDE_VISUALS: ModelVisuals = { logo: claudeLogo, color: "#E19238" };

const VISUALS_BY_ID: Record<number, ModelVisuals> = {
  5001: CHATGPT_VISUALS,
  5002: GEMINI_VISUALS,
  5003: CLAUDE_VISUALS,
};

function normalizeModelName(value?: string): string {
  return (value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function resolveModelVisuals(model: {
  id: number;
  name?: string;
  displayName?: string;
}): ModelVisuals | undefined {
  const byId = VISUALS_BY_ID[model.id];
  if (byId) return byId;
  const name =
    normalizeModelName(model.displayName) || normalizeModelName(model.name);
  if (name.includes("chatgpt") || name.startsWith("gpt") || name.includes("openai")) {
    return CHATGPT_VISUALS;
  }
  if (name.includes("gemini")) return GEMINI_VISUALS;
  if (name.includes("claude") || name.includes("anthropic")) return CLAUDE_VISUALS;
  return undefined;
}

/**
 * An AI assistant (ChatGPT, Gemini, Claude) shaped like a `Brand` just
 * enough to flow through the same generic entity pipeline (`entities[key]`
 * lookups in the response formatters, `EntityIcon`/`getIconForEntity` for
 * the logo, `CardHeader`/gauge for the color) that brands already use.
 * `id` is the integer model id the API expects on `models: number[]`
 * (mirrors `Brand.id`).
 */
export interface AiModel {
  id: number;
  name: string;
  displayName: string;
  logo: string;
  color: string;
  type: "ASSISTANT";
}

export interface AiModelsState {
  ids: number[];
  entities: Record<number, AiModel>;
}

/**
 * Shape of a bootstrap `aiModels` entry. `logo` and `color` on the wire are
 * placeholders today — deliberately not consumed (see `VISUALS_BY_ID`);
 * `setAiModels` keeps the API's id/name and overlays the local icon + color
 * for ChatGPT / Gemini / Claude.
 */
export type AiModelDto = Omit<AiModel, "logo" | "type" | "color"> & {
  logo?: string;
  color?: string;
  type?: "ASSISTANT";
};

// Each brand color pre-blended to ~80% opacity over white — full-strength,
// these read as harsh once several bars sit side by side in a chart. The
// icon badges keep the solid color (see the .svg files); this softened
// value is only for the chart/gauge/badge fill that flows through
// `entities[key].color`. Plain 6-digit hex (not an alpha channel) so every
// consumer of this field — including AiVisibilityGauge's own RRGGBB-only
// hex math — keeps working unchanged.
const MODELS: AiModel[] = [
  {
    id: 5001,
    name: "ChatGPT",
    displayName: "ChatGPT",
    logo: CHATGPT_VISUALS.logo,
    color: CHATGPT_VISUALS.color,
    type: "ASSISTANT",
  },
  {
    id: 5002,
    name: "Gemini",
    displayName: "Gemini",
    logo: GEMINI_VISUALS.logo,
    color: GEMINI_VISUALS.color,
    type: "ASSISTANT",
  },
  {
    id: 5003,
    name: "Claude",
    displayName: "Claude",
    logo: CLAUDE_VISUALS.logo,
    color: CLAUDE_VISUALS.color,
    type: "ASSISTANT",
  },
];

const initialState: AiModelsState = {
  ids: MODELS.map((model) => model.id),
  entities: Object.fromEntries(MODELS.map((model) => [model.id, model])),
};

const aiModelsSlice = createSlice({
  name: "aiModels",
  initialState,
  reducers: {
    reorderAiModels: (state, action: PayloadAction<number[]>) => {
      const incoming = action.payload.filter((id) => state.entities[id]);
      const remaining = state.ids.filter((id) => !incoming.includes(id));
      state.ids = [...incoming, ...remaining];
    },
    /**
     * Sourced from the unified bootstrap API (`clients[0].aiModels`),
     * parallel to `setDistributionsCatalog` / `setQuestionsCatalog`. A
     * null/empty payload (API omits the field, or bootstrap hasn't resolved)
     * leaves the hardcoded `MODELS` default in place rather than clearing
     * the dropdown to empty — likewise when the API sends entries but none
     * carry a valid numeric `id` yet (e.g. the backend hasn't been updated
     * to emit integer model ids): keeping the corrupt catalog would poison
     * every downstream consumer (dropdown option values, `models: number[]`
     * pruning, the request payload) with `null`/`NaN` ids instead of a
     * working fallback.
     */
    setAiModels: (state, action: PayloadAction<AiModelDto[] | null>) => {
      const models = action.payload?.filter(
        (model) => typeof model.id === "number" && Number.isFinite(model.id),
      );
      if (!models || models.length === 0) return;
      state.ids = models.map((model) => model.id);
      state.entities = Object.fromEntries(
        models.map((model): [number, AiModel] => {
          // Bootstrap `logo` is still a placeholder — ChatGPT / Gemini / Claude
          // always get the local icon. `color`, though, is a real per-user
          // saved preference (see updateAiModelColor/updatePreferences) once
          // the API sends one — it must win over the local default, or a
          // saved color would revert on every reload.
          const { logo: _logo, color: apiColor, ...rest } = model;
          const visuals = resolveModelVisuals(model);
          return [
            model.id,
            {
              ...rest,
              type: "ASSISTANT",
              logo: visuals?.logo ?? "",
              color: apiColor || visuals?.color || "",
            },
          ];
        }),
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateAiModelColor.fulfilled, (state, action) => {
      if (action.payload !== null) {
        const model = state.entities[action.payload.modelId];
        if (model) model.color = action.payload.color;
      }
    });
  },
});

export const { setAiModels, reorderAiModels } = aiModelsSlice.actions;
export default aiModelsSlice.reducer;
